import { Table } from '@dynamika-shared/ui/Table/Table';
import { qcListColumns } from './QCList.table.config';
import { useBreadcrumbs } from '@hooks/useBreadcrumbs';
import { QcListFilters } from './QCListFilters';
import { useEffect, useMemo } from 'react';
import { SiteType } from '@dynamika-shared/types/site';
import { prepareUrl } from '@utils/routerHelpers';
import { routesPath } from '@router/routes';
import { useNavigate } from 'react-router-dom';
import { useQCTasksListSlice } from '@restore/QCTasksList/QCTasksList.slice';
import { useClinicalTrialContextSlice } from '@restore/clinicalTrialContext/clinicalTrialContext.slice';
import { useMakePagination } from '@restore/paginationAndSort/paginationAndSort.slice';
import { ExportCsvButton } from '@components/ExportCsvButton';
import { useCsvExport, toCsvColumns } from '@hooks/useCsvExport';
import { QCListTableItem } from '@dynamika-shared/types/qc';

const findSite = (siteId: string) => (site: SiteType) => site.id === siteId;

export const QCList = () => {
  const [qcListData, loading, filters, fetchQcListData, setFilter] =
    useQCTasksListSlice((state, actions) => [
      state.data,
      state.loading,
      state.filters,
      actions.fetchQCTasksList,
      actions.setFilters,
    ]);

  const contextData = useClinicalTrialContextSlice(state => state.data);

  useEffect(() => {
    if (contextData?.configuration?.id) {
      fetchQcListData({
        clinicalTrialId: contextData.configuration.id,
        filters,
      });
    }
  }, [contextData, contextData?.configuration?.id, filters]);

  const columns = useMemo(
    () =>
      contextData?.configuration?.id
        ? qcListColumns(contextData.configuration.id)
        : [],
    [contextData?.configuration?.id]
  );

  const tableData = useMemo(
    () =>
      qcListData?.content?.map(task => {
        return {
          ...task,
          siteCode: contextData?.configuration?.sites?.find(
            findSite(task.siteId)
          )?.code,
        };
      }) || [],
    [contextData, qcListData?.content]
  );

  useBreadcrumbs([['QC List', 'qcTasks']], [contextData?.configuration?.id]);

  const navigate = useNavigate();

  const { exportCsv, isExporting } = useCsvExport<QCListTableItem>();

  const handleExport = () =>
    exportCsv({
      columns: toCsvColumns(columns),
      rows: tableData,
      fileNamePrefix: 'Dynamika_QC_List',
    });
    
  const pagination = useMakePagination(
    'QCList',
    !!tableData.length,
    (page, pageSize) => {
      setFilter({ filterQuery: 'size', filterValue: pageSize });
      setFilter({ filterQuery: 'page', filterValue: page });
    }
  );

  return (
    <>
      <div className='flex items-center justify-between py-4.5'>
        <h1>QC List</h1>
          <ExportCsvButton
            onExport={handleExport}
            isExporting={isExporting}
            disabled={!tableData.length}
          />
      </div>
      <QcListFilters />
      <Table
        loading={loading}
        columns={columns}
        data={tableData}
        pagination={pagination}
        totalItems={qcListData?.totalElements ?? 0}
        sortState={filters.sort}
        onSort={(colId, sort) => {
          const sortValue = sort ? `${colId},${sort}` : '';
          setFilter({ filterQuery: 'sort', filterValue: sortValue });
        }}
        onRowClick={row => {
          if (contextData?.configuration?.id && typeof row !== 'number') {
            navigate(
              prepareUrl(routesPath.qcTaskInfo, [
                contextData?.configuration?.id,
                row?.id,
              ])
            );
          }
        }}
      />
    </>
  );
};
